import { AxiosInstance, CancelToken } from "axios";
import {
  Order,
} from "@/types";

export class DeliveryApi {
  protected sessionToken: string | null = null;
  protected placeIdMap: Map<string, string> = new Map();

  constructor(protected axios: AxiosInstance) {}

  protected extractApartmentSuffix(query: string): string | null {
    const match = query.match(/\d+[a-zA-Z]?(-\d+)(?:[,\s]|$)/);
    return match ? match[1] : null;
  }

  protected processSuggestions(
    suggestions: Array<{ placePrediction: { placeId: string; text: { text: string } } }>,
    apartmentSuffix: string | null
  ): string[] {
    this.placeIdMap.clear();
    return suggestions.map((s) => {
      let text = s.placePrediction.text.text;
      const alreadyHasSuffix = /-\d+/.test(text);
      if (apartmentSuffix && !alreadyHasSuffix) {
        text = text.replace(/(\d+[a-zA-Z]?)(,|$)/, `$1${apartmentSuffix}$2`);
      }
      this.placeIdMap.set(text, s.placePrediction.placeId);
      return text;
    });
  }

  getAddresses(
    query: string,
    language: string,
    cancelToken?: CancelToken
  ): Promise<string[]> {
    return this.axios
      .get<{ session_token: string; suggestions: Array<{ placePrediction: { placeId: string; text: { text: string } } }> }>(
        `/api/address/autocomplete`,
        {
          params: {
            input: query,
            city: language, // TODO: quick hack for now so we can use Jurmala quick
            session_token: this.sessionToken ?? undefined,
          },
          cancelToken,
        }
      )
      .then((r) => {
        this.sessionToken = r.data.session_token;
        const apartmentSuffix = this.extractApartmentSuffix(query);
        return this.processSuggestions(r.data.suggestions, apartmentSuffix);
      });
  }

  hasPlaceId(text: string): boolean {
    return this.placeIdMap.has(text);
  }

  validateAddress(text: string, restrictLocation: boolean, city: string): Promise<string | null> {
    const placeId = this.placeIdMap.get(text);
    if (!placeId) {
      return Promise.resolve(text);
    }

    return this.axios
      .post<{ formattedAddress: string | null }>(`/api/address/autocomplete/validate`, {
          place_id: placeId,
          session_token: this.sessionToken,
          restrict_location: restrictLocation,
          city: city,
      })
      .then((r) => {
        this.sessionToken = null;
        const formattedAddress = r.data.formattedAddress;
        if (!formattedAddress) {
          return formattedAddress;
        }
        const suffix = this.extractApartmentSuffix(text);
        if (suffix && !this.extractApartmentSuffix(formattedAddress)) {
          return formattedAddress.replace(/(\d+[a-zA-Z]?)(,|$)/, `$1${suffix}$2`);
        }
        return formattedAddress;
      });
  }

  removeDelivery(reservationId: number): Promise<Order> {
    return this.axios
      .post<{ success: boolean; order: Order }>("/delivery/removeDelivery", {
        reservationId,
      })
      .then((r) => {
        if (r.data.success) {
          return r.data.order;
        } else {
          return Promise.reject(r.data);
        }
      });
  }
}
